<template>
  <div class="tasks-page">
    <div class="page-header">
      <h1>Task Management</h1>
      <div class="header-actions">
        <button @click="runAllTasks" :disabled="taskStore.running" class="btn btn-primary btn-lg">
          <span v-if="taskStore.running">⏳</span>
          <span v-else>🚀</span>
          Run All Tasks
        </button>
      </div>
    </div>

    <!-- Task Cards -->
    <div class="tasks-grid">
      <div v-for="task in tasks" :key="task.name" class="task-card">
        <div class="task-header">
          <div class="task-info">
            <h3 class="task-title">{{ task.title }}</h3>
            <p class="task-description">{{ task.description }}</p>
          </div>
          <div class="task-icon">{{ task.icon }}</div>
        </div>

        <div class="task-stats">
          <div class="stat">
            <span class="stat-label">Interval:</span>
            <span class="stat-value">{{ task.interval }}</span>
          </div>
          <div class="stat">
            <span class="stat-label">Last Run:</span>
            <span class="stat-value">{{ getLastRunTime(task.name) }}</span>
          </div>
          <div class="stat" v-if="getTaskResult(task.name)">
            <span class="stat-label">Last Result:</span>
            <span class="stat-value">{{ getTaskResult(task.name) }}</span>
          </div>
        </div>

        <div class="task-status" :class="getTaskStatusClass(task.name)">
          {{ getTaskStatus(task.name) }}
        </div>

        <div class="task-actions">
          <button 
            @click="runTask(task.name)" 
            :disabled="taskStore.running"
            class="btn btn-primary"
          >
            <span v-if="isTaskRunning(task.name)">⏳ Running...</span>
            <span v-else>▶️ Run Task</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Task History -->
    <div class="card mt-3">
      <div class="card-header">
        <h3 class="card-title">Task History</h3>
        <button @click="taskStore.clearHistory" class="btn btn-outline btn-sm">
          Clear History
        </button>
      </div>
      <div class="task-history">
        <div v-if="taskStore.taskHistory.length === 0" class="empty-state">
          No task history available
        </div>
        <div v-else class="history-list">
          <div 
            v-for="(item, index) in taskStore.taskHistory" 
            :key="index"
            class="history-item"
            :class="getHistoryItemClass(item.status)"
          >
            <div class="history-header">
              <span class="history-task">{{ getTaskTitle(item.task) }}</span>
              <span class="history-time">{{ formatDateTime(item.timestamp) }}</span>
            </div>
            <div class="history-status">
              <span class="status-badge" :class="getStatusBadgeClass(item.status)">
                {{ item.status }}
              </span>
              <span v-if="item.result" class="history-result">
                {{ formatTaskResult(item.result) }}
              </span>
            </div>
            <div v-if="item.error" class="history-error">
              ❌ {{ item.error }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- System Information -->
    <div class="grid grid-2 mt-3">
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Scheduler Settings</h3>
        </div>
        <div class="settings-list" v-if="appStore.status">
          <div class="setting-item">
            <span class="setting-label">Fetch Orders Interval:</span>
            <span class="setting-value">30 minutes</span>
          </div>
          <div class="setting-item">
            <span class="setting-label">Sync Orders Interval:</span>
            <span class="setting-value">15 minutes</span>
          </div>
          <div class="setting-item">
            <span class="setting-label">Check Shipments Interval:</span>
            <span class="setting-value">60 minutes</span>
          </div>
          <div class="setting-item">
            <span class="setting-label">Invoice Sync Interval:</span>
            <span class="setting-value">120 minutes</span>
          </div>
          <div class="setting-item">
            <span class="setting-label">Automatic Tasks:</span>
            <span class="setting-value" :class="automaticTasksEnabled ? 'text-success' : 'text-danger'">
              {{ automaticTasksEnabled ? 'Enabled' : 'Disabled' }}
            </span>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Quick Commands</h3>
        </div>
        <div class="command-list">
          <div class="command-item">
            <code>npm run tasks:fetch</code>
            <span class="command-desc">Fetch orders from Mirakl</span>
          </div>
          <div class="command-item">
            <code>npm run tasks:sync</code>
            <span class="command-desc">Sync orders to Billbee</span>
          </div>
          <div class="command-item">
            <code>npm run tasks:check</code>
            <span class="command-desc">Check shipment status</span>
          </div>
          <div class="command-item">
            <code>npm run tasks:invoice</code>
            <span class="command-desc">Sync invoices</span>
          </div>
          <div class="command-item">
            <code>npm run tasks:all</code>
            <span class="command-desc">Run all tasks</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="taskStore.error" class="alert alert-danger mt-3">
      {{ taskStore.error }}
    </div>
  </div>
</template>

<script>
import { useTaskStore } from '@/stores/taskStore'
import { useAppStore } from '@/stores/appStore'
import { onMounted, computed } from 'vue'

export default {
  name: 'Tasks',
  setup() {
    const taskStore = useTaskStore()
    const appStore = useAppStore()
    
    const tasks = [
      {
        name: 'fetch-orders',
        title: 'Fetch Orders',
        description: 'Fetch WAITING_ACCEPTANCE orders from Mirakl marketplace only',
        icon: '📥',
        interval: 'Every 30 minutes'
      },
      {
        name: 'sync-orders',
        title: 'Sync to Billbee',
        description: 'Create SHIPPING orders in Billbee for fulfillment (excludes CANCELED)',
        icon: '🔄',
        interval: 'Every 15 minutes'
      },
      {
        name: 'process-orders',
        title: 'Process Orders',
        description: 'Business logic: SHIPPING orders → track shipments & sync invoices. CANCELED/RECEIVED → skip processing',
        icon: '⚙️',
        interval: 'Every 30 minutes'
      },
      {
        name: 'check-shipments',
        title: 'Check Shipments',
        description: 'Check shipped orders in Billbee and update Mirakl',
        icon: '🚚',
        interval: 'Every 60 minutes'
      },
      {
        name: 'invoice-sync',
        title: 'Invoice Sync',
        description: 'Download invoices from Billbee and upload to Mirakl',
        icon: '📄',
        interval: 'Every 120 minutes'
      }
    ]
    
    const automaticTasksEnabled = computed(() => {
      return appStore.status?.config_status?.automatic_tasks ?? false
    })
    
    onMounted(async () => {
      await appStore.fetchStatus()
    })
    
    return {
      taskStore,
      appStore,
      tasks,
      automaticTasksEnabled
    }
  },
  
  methods: {
    async runTask(taskName) {
      try {
        await this.taskStore.runTask(taskName)
        // Refresh status after task completion
        await this.appStore.fetchStatus()
      } catch (error) {
        console.error('Task failed:', error)
      }
    },
    
    async runAllTasks() {
      await this.runTask('all')
    },
    
    getLastRunTime(taskName) {
      const lastRun = this.appStore.status?.last_run
      if (!lastRun) return 'Never'
      
      const taskKeys = {
        'fetch-orders': ['fetch_orders'],
        'sync-orders': ['sync_to_billbee', 'sync_orders'],
        'process-orders': ['process_orders'],
        'check-shipments': ['check_shipments'],
        'invoice-sync': ['invoice_sync']
      }
      const keys = taskKeys[taskName] || [taskName.replace('-', '_')]
      const time = keys.map(key => lastRun[key]).find(Boolean)
      
      return time ? this.formatDateTime(time) : 'Never'
    },
    
    getTaskResult(taskName) {
      const result = this.taskStore.lastResults[taskName]
      if (!result) return null
      
      if (typeof result === 'object' && result[taskName]) {
        return this.formatTaskResult(result[taskName])
      }
      
      return null
    },
    
    getTaskStatus(taskName) {
      if (this.isTaskRunning(taskName)) {
        return 'Running...'
      }
      
      const result = this.taskStore.lastResults[taskName]
      if (!result) return 'Ready'
      
      return result.status === 'completed' ? 'Completed' : 'Error'
    },
    
    getTaskStatusClass(taskName) {
      if (this.isTaskRunning(taskName)) {
        return 'status-running'
      }
      
      const result = this.taskStore.lastResults[taskName]
      if (!result) return 'status-ready'
      
      return result.status === 'completed' ? 'status-success' : 'status-error'
    },
    
    isTaskRunning(taskName) {
      return this.taskStore.running && this.taskStore.isTaskRunning(taskName)
    },
    
    getTaskTitle(taskName) {
      const task = this.tasks.find(t => t.name === taskName)
      return task ? task.title : taskName
    },
    
    getHistoryItemClass(status) {
      return {
        'history-success': status === 'completed',
        'history-error': status === 'error',
        'history-running': status === 'running'
      }
    },
    
    getStatusBadgeClass(status) {
      return {
        'badge-success': status === 'completed',
        'badge-danger': status === 'error',
        'badge-info': status === 'running'
      }
    },
    
    formatDateTime(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleString('de-DE')
    },
    
    formatTaskResult(result) {
      if (typeof result === 'string') return result
      
      if (result.success !== undefined) {
        return result.success ? '✅ Success' : '❌ Failed'
      }
      
      if (result.data) {
        const data = result.data
        if (data.new_orders !== undefined) {
          return `New: ${data.new_orders}, Updated: ${data.updated_orders || 0}`
        }
        if (data.processed !== undefined) {
          return `Processed: ${data.processed}, Success: ${data.success || 0}`
        }
      }
      
      return 'Completed'
    }
  }
}
</script>

<style scoped>
.tasks-page {
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.page-header h1 {
  margin: 0;
}

.tasks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.task-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  transition: all 0.2s;
  border: 1px solid #dee2e6;
}

.task-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.task-info h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
}

.task-description {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.task-icon {
  font-size: 2rem;
}

.task-stats {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.stat {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
}

.stat-label {
  color: #6c757d;
}

.stat-value {
  font-weight: 500;
}

.task-status {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  text-align: center;
  margin-bottom: 1rem;
}

.status-ready {
  background-color: #e2e3e5;
  color: #41464b;
}

.status-running {
  background-color: #cff4fc;
  color: #055160;
}

.status-success {
  background-color: #d1e7dd;
  color: #0f5132;
}

.status-error {
  background-color: #f8d7da;
  color: #721c24;
}

.task-actions {
  display: flex;
  gap: 0.5rem;
}

.task-history {
  max-height: 400px;
  overflow-y: auto;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.history-item {
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.history-item.history-success {
  border-left: 4px solid #198754;
  background-color: #f8fff9;
}

.history-item.history-error {
  border-left: 4px solid #dc3545;
  background-color: #fff5f5;
}

.history-item.history-running {
  border-left: 4px solid #0dcaf0;
  background-color: #f0fcff;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.history-task {
  font-weight: 500;
}

.history-time {
  color: #6c757d;
  font-size: 0.875rem;
}

.history-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.status-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
}

.history-result {
  font-size: 0.875rem;
  color: #6c757d;
}

.history-error {
  color: #dc3545;
  font-size: 0.875rem;
}

.settings-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.setting-label {
  font-weight: 500;
}

.setting-value {
  color: #6c757d;
}

.command-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.command-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.command-item code {
  background-color: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
}

.command-desc {
  font-size: 0.8rem;
  color: #6c757d;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

@media (max-width: 768px) {
  .tasks-grid {
    grid-template-columns: 1fr;
  }
  
  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .task-header {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>